import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";

/** Distancia minima (px) del arrastre para considerarlo gesto (abrir, minimizar o cerrar). */
const SWIPE_THRESHOLD = 40;

/** Desplazamiento maximo (px) hacia arriba que sigue al dedo (con resistencia). */
const MAX_DRAG_UP = 80;

/** Duracion (ms) de la salida hacia abajo antes de emitir "swipeDown". */
const DISMISS_DURATION = 220;

/**
 * Gestos de arrastre para hojas inferiores (bottom sheets).
 *
 * Mientras se arrastra, la hoja sigue al dedo: hacia arriba con resistencia y
 * hacia abajo 1:1. Al soltar, si se movio mas de SWIPE_THRESHOLD px emite
 * "swipeUp" o "swipeDown"; si no, regresa a su lugar. Solo escucha eventos
 * touch, asi que en escritorio no hace nada. Los listeners corren fuera de la
 * zona de Angular y solo se vuelve a entrar al emitir.
 *
 * Usos:
 *   - Hoja de "Operaciones multiples" (bottom-sheet.scss), en su header:
 *       <div class="card-header" appSheetSwipe
 *            (swipeUp)="abrir()" (swipeDown)="minimizar()">
 *   - Paneles inferiores ".content-panel" (NAS-SI, siembra, historial...), en
 *     el propio panel; solo se arrastra desde su ".modal-header" para no pelear
 *     con el scroll del contenido, y al cerrar sale deslizandose hacia abajo:
 *       <div class="content-panel" appSheetSwipe sheetSelector=".content-panel"
 *            handleSelector=".modal-header" [dismissOnSwipeDown]="true"
 *            (swipeDown)="cerrar()">
 */
@Directive({
  selector: "[appSheetSwipe]"
})
export class SheetSwipeDirective implements OnInit, OnDestroy {
  /** Selector de la hoja que se mueve (se busca con closest desde el host, incluido). */
  @Input() sheetSelector = ".floating-selected";

  /** Si se indica, el arrastre solo inicia cuando el toque empieza dentro de este selector. */
  @Input() handleSelector: string | null = null;

  /** Al soltar hacia abajo, la hoja sale deslizandose antes de emitir "swipeDown". */
  @Input() dismissOnSwipeDown = false;

  /** Se emite cuando el usuario arrastra la hoja hacia arriba lo suficiente. */
  @Output() swipeUp = new EventEmitter<void>();

  /** Se emite cuando el usuario arrastra la hoja hacia abajo lo suficiente. */
  @Output() swipeDown = new EventEmitter<void>();

  private startY: number | null = null;
  private deltaY = 0;
  private sheet: HTMLElement | null = null;
  private dismissTimer: any = null;

  constructor(private el: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  /**
   * Registra los listeners touch fuera de la zona de Angular.
   * @returns void
   */
  ngOnInit(): void {
    const host = this.el.nativeElement;
    this.ngZone.runOutsideAngular(() => {
      host.addEventListener("touchstart", this.onTouchStart, { passive: true });
      host.addEventListener("touchmove", this.onTouchMove, { passive: true });
      host.addEventListener("touchend", this.onTouchEnd);
      host.addEventListener("touchcancel", this.onTouchEnd);
    });
  }

  /**
   * Limpia los listeners y cualquier desplazamiento pendiente.
   * @returns void
   */
  ngOnDestroy(): void {
    const host = this.el.nativeElement;
    host.removeEventListener("touchstart", this.onTouchStart);
    host.removeEventListener("touchmove", this.onTouchMove);
    host.removeEventListener("touchend", this.onTouchEnd);
    host.removeEventListener("touchcancel", this.onTouchEnd);
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
    }
    this.resetSheet();
  }

  // Guarda el punto de inicio y la hoja a mover (solo si el toque empezo en el "handle")
  private onTouchStart = (event: TouchEvent): void => {
    const target = event.target as HTMLElement;
    if (this.handleSelector && !(target && target.closest(this.handleSelector))) {
      this.startY = null;
      return;
    }
    this.startY = event.touches[0].clientY;
    this.deltaY = 0;
    this.sheet = this.el.nativeElement.closest(this.sheetSelector) as HTMLElement;
    if (this.sheet) {
      this.sheet.style.transition = "none";
      // Paneles: su animacion de entrada pisaria el transform del arrastre. No se
      // restaura (volver a ponerla reiniciaria la entrada); la hoja de operaciones
      // multiples no la toca porque cambia de animacion al abrir/minimizar.
      if (this.dismissOnSwipeDown) {
        this.sheet.style.animation = "none";
      }
    }
  };

  // La hoja sigue al dedo: hacia arriba con resistencia, hacia abajo 1:1
  private onTouchMove = (event: TouchEvent): void => {
    if (this.startY === null) {
      return;
    }
    this.deltaY = event.touches[0].clientY - this.startY;
    if (this.sheet) {
      const offset = this.deltaY < 0 ? Math.max(-MAX_DRAG_UP, this.deltaY / 2) : this.deltaY;
      this.sheet.style.transform = `translate3d(0, ${offset}px, 0)`;
    }
  };

  // Al soltar: sale hacia abajo, regresa a su lugar o emite segun el umbral
  private onTouchEnd = (): void => {
    if (this.startY === null) {
      return;
    }
    const delta = this.deltaY;
    this.startY = null;
    this.deltaY = 0;

    if (delta >= SWIPE_THRESHOLD && this.dismissOnSwipeDown && this.sheet) {
      this.slideOutAndEmit(this.sheet);
      return;
    }

    this.resetSheet();
    if (delta <= -SWIPE_THRESHOLD) {
      this.ngZone.run(() => this.swipeUp.emit());
      return;
    }
    if (delta >= SWIPE_THRESHOLD) {
      this.ngZone.run(() => this.swipeDown.emit());
    }
  };

  // Termina de bajar la hoja fuera de la pantalla y despues avisa para cerrarla
  private slideOutAndEmit(sheet: HTMLElement): void {
    sheet.style.transition = `transform ${DISMISS_DURATION}ms ease-in`;
    sheet.style.transform = "translate3d(0, 100%, 0)";
    this.dismissTimer = setTimeout(() => {
      this.dismissTimer = null;
      this.ngZone.run(() => this.swipeDown.emit());
    }, DISMISS_DURATION);
  }

  // Quita los estilos en linea para que vuelva a mandar el SCSS
  private resetSheet(): void {
    if (!this.sheet) {
      return;
    }
    this.sheet.style.transition = "";
    this.sheet.style.transform = "";
    this.sheet = null;
  }
}
